(() => {
  'use strict';

  const CHECKOUT_KEY = 'vc_hot_activity_checkout_started';
  const CHECKOUT_AT_KEY = 'vc_hot_activity_checkout_started_at';
  const CLOSE_COUNT_KEY = 'vc_hot_activity_close_count';
  const SILENCE_UNTIL_KEY = 'vc_hot_activity_silence_until';
  const HISTORY_KEY = 'vc_hot_activity_recentActivityHistory';
  const CHECKOUT_TTL = 24 * 60 * 60 * 1000;
  const CHECKOUT_CLOCK_SKEW = 5 * 60 * 1000;
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let controller = null;

  const memorySessionStore = new Map();
  const memoryLocalStore = new Map();

  const createStorage = (name, memoryStore) => {
    let nativeStore = null;
    let nativeAvailable = true;

    try {
      nativeStore = window[name];
    } catch (_error) {
      nativeAvailable = false;
    }

    return {
      get(key) {
        if (nativeAvailable && nativeStore) {
          try {
            const value = nativeStore.getItem(key);
            if (value === null) memoryStore.delete(key);
            else memoryStore.set(key, value);
            return value;
          } catch (_error) {
            nativeAvailable = false;
          }
        }

        return memoryStore.has(key) ? memoryStore.get(key) : null;
      },
      set(key, value) {
        const normalizedValue = String(value);
        memoryStore.set(key, normalizedValue);

        if (!nativeAvailable || !nativeStore) return;
        try {
          nativeStore.setItem(key, normalizedValue);
        } catch (_error) {
          nativeAvailable = false;
        }
      },
      remove(key) {
        memoryStore.delete(key);
        if (!nativeAvailable || !nativeStore) return;

        try {
          nativeStore.removeItem(key);
        } catch (_error) {
          nativeAvailable = false;
        }
      },
    };
  };

  const sessionStore = createStorage('sessionStorage', memorySessionStore);
  const localStore = createStorage('localStorage', memoryLocalStore);

  const isCheckoutControl = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest([
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href*="/checkout"]',
      '[data-checkout-button]',
      '[data-testid="Checkout-button"]',
    ].join(',')));
  };

  const markCheckoutStarted = () => {
    localStore.set(CHECKOUT_AT_KEY, Date.now());
    localStore.set(CHECKOUT_KEY, '1');
    controller?.stopForCheckout();
  };

  document.addEventListener('click', (event) => {
    if (isCheckoutControl(event.target)) markCheckoutStarted();
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (isCheckoutControl(submitter) || (form instanceof HTMLFormElement && /\/checkout(?:$|[/?#])/i.test(form.action))) {
      markCheckoutStarted();
    }
  }, true);

  const root = document.querySelector('[data-vc-hot-activity]');
  if (!root) return;

  const elements = {
    link: root.querySelector('[data-vc-hot-activity-link]'),
    close: root.querySelector('[data-vc-hot-activity-close]'),
    image: root.querySelector('[data-vc-hot-activity-image]'),
    headline: root.querySelector('[data-vc-hot-activity-headline]'),
    product: root.querySelector('[data-vc-hot-activity-product]'),
    time: root.querySelector('[data-vc-hot-activity-time]'),
    productData: root.querySelector('[data-vc-hot-activity-products]'),
  };

  let products = [];
  try {
    products = JSON.parse(elements.productData?.textContent || '[]').filter((product) => (
      product && typeof product.title === 'string' && typeof product.url === 'string'
    ));
  } catch (_error) {
    products = [];
  }
  if (
    !products.length
    || !elements.link
    || !elements.close
    || !elements.headline
    || !elements.product
    || !elements.time
  ) return;

  const cities = [
    'London', 'Manchester', 'Birmingham', 'Leeds', 'Bristol', 'Liverpool', 'Sheffield',
    'Nottingham', 'Leicester', 'Coventry', 'Cardiff', 'Glasgow', 'Edinburgh', 'Newcastle',
  ];
  const messages = [
    { icon: '🔥', text: '正在热卖' },
    { icon: '⭐', text: '热门选择' },
    { icon: '🔥', text: '近期人气很高' },
    { icon: '🔥', text: '当前人气商品' },
    { icon: '⚡', text: '最近很受欢迎' },
    { icon: '🔥', text: '热度持续上升' },
    { icon: '⭐', text: '人气推荐' },
    { icon: '⚡', text: '正在成为热门' },
    { icon: '🔥', text: '近期关注度高' },
  ];
  const times = [
    '刚刚更新', '1分钟前更新', '2分钟前更新', '3分钟前更新',
    '5分钟前更新', '8分钟前更新', '10分钟前更新', '15分钟前更新',
  ];
  const timers = { show: 0, hide: 0, transition: 0, recovery: 0, frame: 0 };
  let visible = false;
  let stopped = Number(sessionStore.get(CLOSE_COUNT_KEY) || 0) >= 2;
  let checkoutHold = false;
  let recoveryPending = false;

  const randomMs = (minimumSeconds, maximumSeconds) => (
    Math.round((minimumSeconds + Math.random() * (maximumSeconds - minimumSeconds)) * 1000)
  );

  const clearTimer = (name) => {
    if (timers[name]) {
      if (name === 'frame') window.cancelAnimationFrame(timers[name]);
      else window.clearTimeout(timers[name]);
    }
    timers[name] = 0;
  };

  const clearTimers = () => {
    clearTimer('show');
    clearTimer('hide');
    clearTimer('transition');
    clearTimer('recovery');
    clearTimer('frame');
    recoveryPending = false;
  };

  const hide = (immediate = false) => {
    clearTimer('hide');
    clearTimer('transition');
    clearTimer('frame');
    visible = false;
    root.classList.remove('is-visible');
    root.setAttribute('aria-hidden', 'true');

    if (immediate) {
      root.classList.remove('is-leaving');
      root.hidden = true;
      return;
    }

    root.classList.add('is-leaving');
    timers.transition = window.setTimeout(() => {
      root.classList.remove('is-leaving');
      root.hidden = true;
      timers.transition = 0;
    }, 190);
  };

  const readHistory = () => {
    try {
      const history = JSON.parse(sessionStore.get(HISTORY_KEY) || '[]');
      return Array.isArray(history)
        ? history.filter((entry) => entry && typeof entry === 'object').slice(0, 3)
        : [];
    } catch (_error) {
      return [];
    }
  };

  const randomItem = (items) => items[Math.floor(Math.random() * items.length)];

  const chooseActivity = () => {
    const history = readHistory();
    const previous = history[0];
    const recentCombinations = new Set(
      history.map((entry) => entry.combination).filter(Boolean)
    );
    const recentFullCombinations = new Set(
      history.map((entry) => {
        if (entry.fullCombination) return entry.fullCombination;
        if (!entry.productUrl || !entry.message || !entry.time) return '';

        return [
          entry.city || 'no-city',
          entry.productUrl,
          entry.message,
          entry.time,
        ].join('|');
      }).filter(Boolean)
    );
    let activity = null;

    for (let attempt = 0; attempt < 10; attempt += 1) {
      const city = Math.random() < 0.6 ? randomItem(cities) : '';
      const product = randomItem(products);
      const message = randomItem(messages);
      const time = randomItem(times);
      const combination = `${city || 'no-city'}|${product.url}`;
      const fullCombination = [
        city || 'no-city',
        product.url,
        message.text,
        time,
      ].join('|');

      activity = { city, product, message, time, combination, fullCombination };

      const repeatsPreviousProduct = product.url === previous?.productUrl;
      const repeatsPreviousHeadline = message.text === previous?.message;
      const repeatsRecentCombination = recentCombinations.has(combination);
      const repeatsRecentFullCombination = recentFullCombinations.has(fullCombination);

      if (
        !repeatsPreviousProduct
        && !repeatsPreviousHeadline
        && !repeatsRecentCombination
        && !repeatsRecentFullCombination
      ) break;
    }

    const record = {
      productUrl: activity.product.url,
      city: activity.city,
      message: activity.message.text,
      combination: activity.combination,
      fullCombination: activity.fullCombination,
      time: activity.time,
    };
    const nextHistory = [record, ...history].slice(0, 3);
    sessionStore.set(HISTORY_KEY, JSON.stringify(nextHistory));
    return activity;
  };

  const hasBlockingInteraction = () => {
    const active = document.activeElement;
    return Boolean(
      active instanceof Element
      && active.closest('select, .bx-widget, [role="combobox"], [role="listbox"], details[open]')
    );
  };

  const canDisplay = () => (
    !stopped
    && !checkoutHold
    && !recoveryPending
    && mobileQuery.matches
    && !document.hidden
    && !hasBlockingInteraction()
  );

  const renderActivity = (activity) => {
    const url = new URL(activity.product.url, window.location.origin);
    const safeUrl = url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : '/';
    const city = activity.city ? `${activity.city} · ` : '';

    elements.headline.textContent = `${activity.message.icon} ${city}${activity.message.text}`;
    elements.product.textContent = activity.product.title;
    elements.time.textContent = activity.time;
    elements.link.href = safeUrl;
    elements.link.setAttribute('aria-label', `${elements.headline.textContent}，${elements.product.textContent}`);

    if (activity.product.image) {
      elements.image.src = activity.product.image;
      root.classList.remove('has-no-image');
    } else {
      elements.image.removeAttribute('src');
      root.classList.add('has-no-image');
    }
  };

  const schedule = (minimumSeconds, maximumSeconds) => {
    clearTimer('show');
    if (stopped || checkoutHold || recoveryPending || !mobileQuery.matches || document.hidden) return;

    const silenceUntil = Number(sessionStore.get(SILENCE_UNTIL_KEY) || 0);
    const delay = Math.max(randomMs(minimumSeconds, maximumSeconds), silenceUntil - Date.now(), 0);
    timers.show = window.setTimeout(() => {
      timers.show = 0;
      if (!canDisplay()) {
        schedule(15, 35);
        return;
      }

      renderActivity(chooseActivity());
      root.hidden = false;
      root.classList.remove('is-leaving');
      root.setAttribute('aria-hidden', 'false');
      timers.frame = window.requestAnimationFrame(() => {
        timers.frame = 0;
        if (visible) root.classList.add('is-visible');
      });
      visible = true;

      timers.hide = window.setTimeout(() => {
        hide();
        schedule(12, 20);
      }, randomMs(3.8, 4.5));
    }, delay);
  };

  const pauseForInteraction = () => {
    clearTimer('show');
    if (visible) hide();
    if (!recoveryPending) schedule(15, 35);
  };

  const clearCheckoutFlag = () => {
    localStore.remove(CHECKOUT_KEY);
    localStore.remove(CHECKOUT_AT_KEY);
  };

  const startCheckoutRecovery = () => {
    if (localStore.get(CHECKOUT_KEY) !== '1') {
      clearTimer('recovery');
      recoveryPending = false;
      checkoutHold = false;
      return false;
    }

    const now = Date.now();
    const startedAt = Number(localStore.get(CHECKOUT_AT_KEY));
    const timestampIsValid = (
      Number.isFinite(startedAt)
      && startedAt > 0
      && startedAt <= now + CHECKOUT_CLOCK_SKEW
      && now - startedAt <= CHECKOUT_TTL
    );

    if (!timestampIsValid) {
      clearTimer('recovery');
      recoveryPending = false;
      clearCheckoutFlag();
      checkoutHold = false;
      return false;
    }

    if (recoveryPending && timers.recovery) {
      clearTimer('show');
      hide(true);
      return true;
    }

    checkoutHold = true;
    clearTimers();
    hide(true);
    recoveryPending = false;
    if (Number(root.dataset.cartCount || 0) <= 0) return true;

    recoveryPending = true;
    checkoutHold = false;
    timers.recovery = window.setTimeout(() => {
      timers.recovery = 0;
      recoveryPending = false;
      checkoutHold = false;
      clearCheckoutFlag();
      resumeFlow(0, 0);
    }, randomMs(45, 90));
    return true;
  };

  controller = {
    stopForCheckout() {
      checkoutHold = true;
      recoveryPending = false;
      clearTimers();
      hide(true);
    },
  };

  const readCloseCount = () => {
    const closeCount = Number(sessionStore.get(CLOSE_COUNT_KEY));
    return Number.isFinite(closeCount) && closeCount >= 0 ? closeCount : 0;
  };

  const syncStoppedState = () => {
    if (readCloseCount() >= 2) stopped = true;
    if (!stopped) return false;

    clearTimers();
    hide(true);
    return true;
  };

  const resumeFlow = (minimumSeconds, maximumSeconds) => {
    if (syncStoppedState()) return;
    if (!mobileQuery.matches || document.hidden) {
      clearTimers();
      hide(true);
      return;
    }
    if (!startCheckoutRecovery()) schedule(minimumSeconds, maximumSeconds);
  };

  elements.close.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const closeCount = readCloseCount() + 1;
    sessionStore.set(CLOSE_COUNT_KEY, closeCount);
    hide();

    if (closeCount >= 2) {
      stopped = true;
      clearTimers();
      hide(true);
      return;
    }

    sessionStore.set(SILENCE_UNTIL_KEY, Date.now() + 60000);
    schedule(60, 60);
  });

  elements.link.addEventListener('click', () => {
    sessionStore.set(SILENCE_UNTIL_KEY, Date.now() + randomMs(16, 26));
  });

  document.addEventListener('focusin', (event) => {
    if (event.target instanceof Element && event.target.closest('select, .bx-widget, [role="combobox"], [role="listbox"]')) {
      pauseForInteraction();
    }
  });

  document.addEventListener('focusout', (event) => {
    if (event.target instanceof Element && event.target.closest('select, .bx-widget, [role="combobox"], [role="listbox"]')) {
      window.setTimeout(() => {
        if (!hasBlockingInteraction()) resumeFlow(15, 35);
      }, 0);
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (event.target instanceof Element && event.target.closest('select, .bx-widget, [role="combobox"], [role="listbox"], summary')) {
      pauseForInteraction();
    }
  }, { passive: true });

  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('select, .bx-widget, [role="combobox"], [role="listbox"]')) {
      pauseForInteraction();
    }
  });

  document.addEventListener('toggle', (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (event.target.open) {
      pauseForInteraction();
    } else {
      resumeFlow(15, 35);
    }
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimers();
      hide(true);
    } else {
      resumeFlow(15, 35);
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      clearTimers();
      hide(true);
      resumeFlow(15, 35);
    }
  });

  mobileQuery.addEventListener('change', (event) => {
    if (!event.matches) {
      clearTimers();
      hide(true);
    } else {
      resumeFlow(15, 35);
    }
  });

  resumeFlow(6, 9);
})();
